import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useT } from "../i18n/LanguageContext";

export default function About() {
  const t = useT();
  const root = useReveal();
  const roles = t("about.creator.roles");
  const paragraphs = t("about.creator.paragraphs");
  const stats = t("about.stats");
  const meaning = t("about.meaning");

  return (
    <div ref={root} className="pt-32 md:pt-40">
      {/* HERO */}
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="reveal eyebrow">{t("about.eyebrow")}</p>
            <h1 className="reveal reveal-delay-1 mt-8 font-serif text-5xl md:text-7xl lg:text-[6.5rem] tracking-tight leading-[0.95] text-[#1A1A1A]">
              {t("about.title")}
              <br />
              <em className="text-[#9A7E47] not-italic font-light italic">
                {t("about.titleEm")}
              </em>
            </h1>
          </div>
          <div className="md:col-span-4 md:pb-3">
            <p className="reveal reveal-delay-2 text-lg text-[#4A4A4A] leading-relaxed">
              {t("about.intro")}
            </p>
          </div>
        </div>
      </section>

      {/* MEANING OF SANITY */}
      <section
        className="container-x py-24 md:py-32"
        data-testid="about-meaning"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* image */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="reveal relative aspect-[4/5] overflow-hidden surface elev-1">
              <img
                src="/images/eva_blossom.jpg"
                alt="Almond blossoms, Tenerife"
                className="absolute inset-0 w-full h-full object-cover img-tint"
              />
            </div>
            <p className="mt-6 text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
              <span className="tri inline-block mr-2 align-middle" />
              Almonds in bloom · Tenerife
            </p>
          </div>

          {/* text */}
          <div className="lg:col-span-7">
            <p className="reveal eyebrow">{meaning.eyebrow}</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
              {meaning.title}
            </h2>
            <p className="reveal reveal-delay-2 mt-8 text-lg text-[#4A4A4A] leading-relaxed">
              {meaning.lead}
            </p>
            <div className="reveal reveal-delay-3 mt-8 space-y-5 text-[#4A4A4A] leading-relaxed">
              {meaning.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div className="hairline mt-12" />

            <p className="reveal mt-10 text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
              {meaning.coreEyebrow}
            </p>
            <p
              className="reveal reveal-delay-1 mt-6 font-serif italic text-2xl md:text-3xl leading-[1.25] text-[#1A1A1A] max-w-2xl"
              data-testid="about-meaning-core"
            >
              {meaning.coreLine}
            </p>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
              {meaning.triad.map((t, i) => (
                <li
                  key={i}
                  className={`reveal reveal-delay-${(i % 3) + 1} border-t border-[rgba(26,26,26,0.12)] pt-4`}
                  data-testid={`about-meaning-triad-${i + 1}`}
                >
                  <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                    {t.k}
                  </p>
                  <p className="mt-2 text-[#1A1A1A] font-serif italic text-lg leading-snug">
                    {t.v}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT SANITY IS + STATS */}
      <section className="bg-[#F3F1EA] border-y border-[rgba(26,26,26,0.06)]">
        <div className="container-x py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-5">
              <p className="reveal eyebrow">{t("about.whatIs.eyebrow")}</p>
              <h2 className="reveal reveal-delay-1 mt-6 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
                {t("about.whatIs.title")}
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="reveal reveal-delay-2 text-lg text-[#4A4A4A] leading-relaxed">
                {t("about.whatIs.body")}
              </p>
            </div>
          </div>

          <div className="hairline mt-20" />

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-10">
            {stats.map((s, i) => (
              <div key={i} className="reveal">
                <p className="font-serif text-5xl md:text-6xl text-[#9A7E47] tracking-tight">
                  {s.k}
                </p>
                <p className="mt-3 text-[#707070] text-sm leading-relaxed">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUTHOR / CREATOR */}
      <section
        className="container-x py-32 md:py-40"
        data-testid="about-creator"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="reveal relative aspect-[4/5] overflow-hidden surface elev-1">
              <img
                src="/images/eva_hero.png"
                alt="Eva Petkoff"
                className="absolute inset-0 w-full h-full object-cover img-tint"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="reveal eyebrow">{t("about.creator.eyebrow")}</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
              {t("about.creator.title")}
            </h2>
            <ul className="reveal reveal-delay-2 mt-6 space-y-2 text-[#4A4A4A]">
              {roles.map((r, i) => (
                <li key={i} className="text-base">
                  <span className="tri inline-block mr-3" />
                  {r}
                </li>
              ))}
            </ul>

            <div className="reveal reveal-delay-3 mt-12 space-y-6 text-lg text-[#4A4A4A] leading-relaxed">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <p
              className="reveal reveal-delay-4 mt-10 font-serif italic text-[#9A7E47] text-lg"
              data-testid="about-creator-signature"
            >
              {t("about.creator.signature")}
            </p>

            <div className="reveal reveal-delay-4 mt-12 flex items-center gap-4 flex-wrap">
              <Link to="/curriculum" className="btn-ink" data-testid="about-cta-curriculum">
                {t("about.ctaPrimary")} <ArrowRight size={16} />
              </Link>
              <Link to="/method" className="btn-ghost" data-testid="about-cta-method">
                {t("about.ctaSecondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
